import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {AutocompleteDropdown} from '../../../common/dropdowns/autocomplete/autocompleteDropdown';
import {AsyncDropdownSource, DropdownFetcher} from '../../../common/dropdowns/source/asyncDropdownSource';
import {SuggestedVisualParticipantsDropdownItem} from './suggestedVisualParticipantsDropdownItem';
import {VisualParticipantDropdownItem} from './visualParticipantDropdownItem';

/*
 * Props.
 */

interface VisualParticipantsDropdownProps extends DropdownProps {
  fetcher: DropdownFetcher<VisualParticipantModel>;
  query: string;
  onVisualParticipantsSelect: HandlerOf<ReadonlyArray<VisualParticipantModel>>;
  suggestedParticipants?: ReadonlyArray<VisualParticipantModel>;
  renderDefaultDropdown?: RendererOf<DropdownProps>;
}

/*
 * Styles.
 */

const StyledFooterDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Components.
 */

export const VisualParticipantsDropdown: FC<VisualParticipantsDropdownProps> = (props) => {
  const {suggestedParticipants} = props;

  if (!suggestedParticipants || suggestedParticipants.length === 0) {
    return <VisualParticipantsDropdownComponent {...props} />;
  }

  return (
    <Dropdown {...props}>
      {renderGroupSuggestion(props, suggestedParticipants)}
      {maybeRenderIndividualSuggestions(props, suggestedParticipants)}
    </Dropdown>
  );
};

const VisualParticipantsDropdownComponent: FC<VisualParticipantsDropdownProps> = (props) => {
  const {fetcher, renderDefaultDropdown, query} = props;

  const [isDefaultDropdownHidden, setIsDefaultDropdownHidden] = useState(false);

  const renderRow = useCallback(
    (visualParticipant: VisualParticipantModel) =>
      renderVisualParticipantDropdownItem(visualParticipant, () => {
        props.onVisualParticipantsSelect([visualParticipant]);
        setIsDefaultDropdownHidden(true);
      }),
    [props],
  );

  const source = useMemo(
    () =>
      new AsyncDropdownSource({
        fetcher,
        renderRow,
      }),
    [fetcher, renderRow],
  );

  if (!query && renderDefaultDropdown && !isDefaultDropdownHidden) {
    return (
      <>
        {renderDefaultDropdown({
          ...props,
          onRequestClose: () => setIsDefaultDropdownHidden(true),
        })}
      </>
    );
  }

  return <AutocompleteDropdown {...props} source={source} />;
};

function renderVisualParticipantDropdownItem(suggestedParticipant: VisualParticipantModel, onClick: Handler) {
  return (
    <VisualParticipantDropdownItem
      key={suggestedParticipant.contact.uid}
      visualParticipant={suggestedParticipant}
      onSelect={onClick}
    />
  );
}

function renderGroupSuggestion(
  props: VisualParticipantsDropdownProps,
  suggestedParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  if (suggestedParticipants.length === 1) {
    // Render the single suggested participant.
    return (
      <DropdownList {...props}>
        {renderVisualParticipantDropdownItem(suggestedParticipants[0], () =>
          props.onVisualParticipantsSelect([suggestedParticipants[0]]),
        )}
      </DropdownList>
    );
  }

  return (
    <DropdownList {...props}>
      <SuggestedVisualParticipantsDropdownItem
        visualParticipants={suggestedParticipants}
        onSelect={() => props.onVisualParticipantsSelect(suggestedParticipants)}
      />
    </DropdownList>
  );
}

function maybeRenderIndividualSuggestions(
  props: VisualParticipantsDropdownProps,
  suggestedParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  // Only create a footer div if there is more than one suggested participant.
  if (suggestedParticipants.length <= 1) {
    return undefined;
  }

  return (
    <StyledFooterDiv>
      <DropdownList>
        {suggestedParticipants.map((p) =>
          renderVisualParticipantDropdownItem(p, () => props.onVisualParticipantsSelect([p])),
        )}
      </DropdownList>
    </StyledFooterDiv>
  );
}
